import { type AbsolutePath, type ImmutableArray, type ImmutableDictionary, type Link, type Nullish, type RelativePath, getPath } from "shelving";

/** Configuration: specifies the settings and metadata for a single page. */
export type MetadataStatus = 100 | 200 | 301 | 302 | 400 | 401 | 403 | 404 | 500;
export type Metadata = {
	readonly status?: MetadataStatus | undefined;
	readonly base?: Link | undefined;
	readonly path?: AbsolutePath | undefined;
	readonly site?: string | undefined; // Site title.
	readonly title?: string | undefined; // Page title.
	readonly description?: string | undefined;
	readonly image?: string | undefined;
	readonly language?: string | undefined;
	readonly csp?: ContentSecurityPolicy | undefined;
	readonly meta?: Metatags | undefined;
	readonly links?: ImmutableDictionary<string> | undefined;
	readonly modules?: ImmutableArray<string> | undefined;
	readonly scripts?: ImmutableArray<string> | undefined;
	readonly styles?: ImmutableArray<string> | undefined;
};
export type PossibleMetadata = {
	readonly path?: RelativePath | AbsolutePath | undefined;
} & Omit<Metadata, "path">;

/** Set of named metatags. */
export type Metatags = ImmutableDictionary<Nullish<string | boolean>>;

/** Type for a deconstructed Content-Security-Policy tag. */
export type ContentSecurityPolicy = { readonly [resource: string]: string[] };

/** Turn a deconstructed CSP into a string. */
export function joinCSP(csp: Nullish<ContentSecurityPolicy>): string | undefined {
	if (typeof csp === "string") return csp;
	if (csp !== null && csp !== undefined) return Object.entries(csp).map(_mapCSP).join("; ");
}
const _mapCSP = ([key, content]: [string, string[]]) => `${key} ${content.join(" ")}`;

/** Merge two titles together. */
export const mergeTitles = (...titles: (string | undefined)[]): string => titles.filter(Boolean).join(" - ");

/**
 * Merge two metadata objects.
 * - `title` is merged, e.g. `Manchester Runners` + `Messages` becomes `Messages - Manchester Runners`
 * - `URL` is resolved to an absolute URL, e.g. `./d/e/f` + `/a/b/c` becomes `https://d.com/a/b/c/d/e/f`
 */
export function mergeMetadata({ title: title1, path: path1, ...metadata1 }: Metadata, { title: title2, path: path2, ...metadata2 }: PossibleMetadata): Metadata {
	const path = path2 ? getPath(path2, path1) : path1;
	return {
		title: mergeTitles(title2, title1),
		path,
		...metadata1,
		...metadata2,
	};
}
